import { useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Alert, AlertIcon, AlertDescription, VStack } from '@chakra-ui/react';

import { apiGetLifeInsuranceFormData } from 'api';

import { useAppSelector, useAppDispatch } from 'hooks';
import {
  initializeForm,
  resetForm,
  setTypeOfPolicy,
} from 'reducers/lifeInsuranceFormReducer';
import { WIP_STATUS_BINDER_PENDING_UPLOAD, WIP_STATUS_BINDER_UPLOADED, WIP_STATUS_COMPLETED, ROLE_ADMIN } from 'variables/constants';
import { AuthContext } from 'contexts/AuthContext';

// Life Insurance Form Sections
import LifeClientInformation from './sections/LifeClientInformation';
import LifePolicySection from './sections/LifePolicySection';
import LifeAdditionalDocumentsAndNotes from './sections/LifeAdditionalDocumentsAndNotes';
import LifeSubmit from './sections/LifeSubmit';
import DisclosureAndAttestation from 'views/shared/DisclosureAndAttestation';
import { APILifeInsuranceForm } from 'types/api.types';

const LifeInsuranceFormView = () => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const isDataSet = useAppSelector((state) => state.lifeInsuranceForm.isDataSet);
  const wipStatus = useAppSelector((state) => state.lifeInsuranceForm.wipStatus);
  const typeOfPolicy = useAppSelector(
    (state) => state.lifeInsuranceForm.data.type_of_policy,
  );

  // Check if form is locked (completed or pending upload)
  const isLocked = wipStatus === WIP_STATUS_BINDER_PENDING_UPLOAD || wipStatus === WIP_STATUS_COMPLETED || wipStatus === WIP_STATUS_BINDER_UPLOADED;
  const isAdmin = user?.role === ROLE_ADMIN;
  const canEditPremium = isAdmin && isLocked;

  // Get selected policy types (keep empty strings to maintain positions)
  const selectedTypes = typeOfPolicy
    ? typeOfPolicy.split(',').map((t) => t.trim())
    : [];

  // Handle removing a policy
  const handleRemovePolicy = (index: number) => {
    const updated = selectedTypes.filter((_, i) => i !== index);
    // Filter out empty strings when removing
    const filtered = updated.filter((v) => v && v.trim().length > 0);
    dispatch(setTypeOfPolicy(filtered.join(',')));
  };

  const { data: initialData, isFetching } = useQuery({
    queryKey: ['life_insurance_form', uuid],
    queryFn: () => apiGetLifeInsuranceFormData(uuid!),
    refetchOnWindowFocus: false,
  }) as {
    error: any;
    data: APILifeInsuranceForm;
    isFetching: boolean;
  };

  useEffect(() => {
    dispatch(resetForm());
  }, [uuid, dispatch]);

  useEffect(() => {
    if (isDataSet || isFetching || !initialData) return;

    // Initialize life insurance form with API data
    dispatch(initializeForm(initialData));
  }, [initialData, uuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet) {
    return null;
  }

  // Ensure at least one policy section exists
  const policyCount = Math.max(1, selectedTypes.length);

  return (
    <>
      {isLocked && isAdmin && (
        <Alert status="warning" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been submitted. As an admin, you may only edit premium fields. 
              All other fields are locked.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isLocked && !isAdmin && (
        <Alert status="info" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been completed and submitted. No further changes can
              be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      <LifeClientInformation isLocked={isLocked} />
      
      {/* Render each policy section */}
      <VStack spacing={4} align="stretch">
        {Array.from({ length: policyCount }).map((_, index) => (
          <LifePolicySection
            key={index}
            policyIndex={index}
            isLocked={isLocked}
            canEditPremium={canEditPremium}
            showRemoveButton={policyCount > 1}
            onRemove={() => handleRemovePolicy(index)}
          />
        ))}
      </VStack>

      <LifeAdditionalDocumentsAndNotes isLocked={isLocked} />

      <DisclosureAndAttestation
        formStateKey="lifeInsuranceForm"
        isLocked={isLocked}
      />
      <LifeSubmit isLocked={isLocked} canEditPremium={canEditPremium} />
    </>
  );
};

export default LifeInsuranceFormView;
